/** @format — Map GET /v1/chats/.../messages rows to chat UI view models */

import { fetchAguiStreamState, fetchTrackingAguiStreamState } from "./aguiStream";
import { normalizeAguiComponents } from "./normalizeComponents";
import { hasKnownAguiComponents } from "./aguiMessageUtils";
import { extractRunUiSnapshots } from "./extractAguiState";
import {
  collectAgentUiBlocks,
  hasExplicitPersistedUi,
  isCasualAgentReply,
  resolveAgentUiForDisplay,
  sanitizeEnrichedChatMessages,
  shouldStripCarriedOverUi,
} from "./aguiDisplayUtils";

const FORWARDED_USER_RE =
  /User selected ['"]([^'"]+)['"]\s+from the port\.recommendation/i;

function tryParseJsonObject(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function extractTextFromContent(content) {
  if (content == null) return "";
  if (typeof content === "string") {
    const parsed = tryParseJsonObject(content);
    if (parsed) return extractTextFromContent(parsed);
    return content;
  }
  if (typeof content !== "object") return String(content);

  const c = /** @type {Record<string, unknown>} */ (content);
  if (typeof c.response === "string") return c.response;
  if (typeof c.message === "string") return c.message;
  if (typeof c.text === "string") return c.text;
  if (typeof c.content === "string") return c.content;
  return "";
}

function coerceUiComponentsRaw(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    }
  }
  return value;
}

/** Merge API message fields that may carry persisted AG-UI metadata */
export function mergeMessageMetadata(msg = {}) {
  const metadata = {};
  const sources = [msg.custom_fields, msg.customFields, msg.metadata];
  for (const src of sources) {
    if (src && typeof src === "object" && !Array.isArray(src)) {
      Object.assign(metadata, src);
    }
  }
  return metadata;
}

function collectUiRawCandidates(content, metadata = {}) {
  const list = [];
  const push = (v) => {
    const coerced = coerceUiComponentsRaw(v);
    if (coerced != null) list.push(coerced);
  };

  push(metadata.ui_components);
  push(metadata.uiComponents);
  push(metadata.agui_ui_components);
  push(metadata.agui?.ui_components);
  push(metadata.generative_ui);
  push(metadata.generativeUi);

  if (content && typeof content === "object") {
    const c = /** @type {Record<string, unknown>} */ (content);
    push(c.ui_components);
    push(c.uiComponents);
    push(c.generative_ui);
    push(c.generativeUi);
    if (c.data && typeof c.data === "object") {
      const d = /** @type {Record<string, unknown>} */ (c.data);
      push(d.ui_components);
      push(d.uiComponents);
    }
  }

  return list;
}

function extractUiComponents(content, metadata = {}) {
  for (const raw of collectUiRawCandidates(content, metadata)) {
    const normalized = normalizeAguiComponents(raw);
    if (normalized.length) return normalized;
  }
  return [];
}

export function extractPersistedUiComponents(content, metadata = {}) {
  return extractUiComponents(content, metadata);
}

function resolveMessagePayload(msg) {
  const metadata = mergeMessageMetadata(msg);

  const revisions = Array.isArray(msg.revisions) ? msg.revisions : [];
  if (revisions.length) {
    for (const rev of revisions) {
      const ui = extractUiComponents(rev.content, metadata);
      if (ui.length) {
        return { content: rev.content, metadata, uiComponents: ui };
      }
    }
    return {
      content: revisions[0].content,
      metadata,
      uiComponents: extractUiComponents(revisions[0].content, metadata),
    };
  }

  let content = msg.content;
  if (typeof content === "string") {
    const parsed = tryParseJsonObject(content);
    if (parsed) content = parsed;
  }

  return {
    content,
    metadata,
    uiComponents: extractUiComponents(content, metadata),
  };
}

export function formatUserMessageText(text, metadata = {}) {
  if (!text || typeof text !== "string") return text ?? "";

  if (metadata?.agui_action?.label) return metadata.agui_action.label;
  if (metadata?.display_text) return metadata.display_text;
  if (metadata?.system_message_type === "agui_action") {
    return metadata.label ?? "Selected an option";
  }

  const match = text.match(FORWARDED_USER_RE);
  if (match) return `Selected: ${match[1]}`;

  if (text.includes("User selected") && text.includes("port.recommendation")) {
    const short = text.match(/User selected ['"]([^'"]+)['"]/);
    return short ? `Selected: ${short[1]}` : "Selected a port option";
  }

  if (text.includes("Selected option context:") && text.length > 200) {
    const short = text.match(/User selected ['"]([^'"]+)['"]/);
    return short ? `Selected: ${short[1]}` : "Selected a port option";
  }

  return text;
}

export function mapChatApiMessageToViewModel(msg) {
  const { content, metadata, uiComponents: presetUi } = resolveMessagePayload(msg);
  const text = extractTextFromContent(content);

  if (msg.is_agent_message) {
    const runId = metadata.run_id ?? metadata.runId ?? null;
    const { uiComponents, isAgui } = resolveAgentUiForDisplay({
      text,
      uiComponents: presetUi,
      metadata,
    });
    return {
      id: msg.id,
      chatId: msg.room_id ?? msg.chat_id ?? null,
      role: "agent",
      text,
      uiComponents,
      isAgui,
      timestamp: new Date(msg.created_at),
      runId,
      metadata,
    };
  }

  return {
    id: msg.id,
    chatId: msg.room_id ?? msg.chat_id ?? null,
    role: "user",
    text: formatUserMessageText(text, metadata),
    timestamp: new Date(msg.created_at),
    metadata,
  };
}

function findPreviousUserMessage(messages, agentIndex) {
  for (let i = agentIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === "user") return messages[i];
  }
  return null;
}

function findPreviousAgentMessage(messages, agentIndex) {
  for (let i = agentIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === "agent") return messages[i];
  }
  return null;
}

/** Match persisted run snapshots to agent messages by run_id only — never attach unrelated UI */
function applyRunSnapshotsToMessages(messages, runSnapshots) {
  if (!runSnapshots.length) return messages;

  const runsById = new Map(
    runSnapshots
      .filter((r) => r.runId)
      .map((r) => [String(r.runId), r.uiComponents]),
  );
  if (!runsById.size) return messages;

  return messages.map((m, index) => {
    if (m.role !== "agent" || m.uiComponents?.length || !m.runId) return m;
    const prevUser = findPreviousUserMessage(messages, index);
    const prevAgent = findPreviousAgentMessage(messages, index);
    const stripCarriedOver = shouldStripCarriedOverUi(messages, index);
    const ui = runsById.get(String(m.runId));
    if (!ui?.length) return m;
    if (isCasualAgentReply(m.text) && !hasExplicitPersistedUi(m.metadata ?? {})) {
      return m;
    }
    const resolved = resolveAgentUiForDisplay({
      text: m.text,
      uiComponents: ui,
      userPrompt: prevUser?.text ?? "",
      metadata: m.metadata ?? {},
      isActionFollowUp: stripCarriedOver,
      previousTurnUi: stripCarriedOver ? collectAgentUiBlocks(prevAgent) : [],
    });
    if (!resolved.uiComponents.length) return m;
    return { ...m, uiComponents: resolved.uiComponents, isAgui: resolved.isAgui };
  });
}

/**
 * Only restore UI that belongs to this message (metadata or matching run_id).
 * Does not use session cache or latest thread snapshot (prevents false AG-UI on "hello").
 */
export async function enrichMessagesWithAguiState(messages, threadId, options = {}) {
  if (!threadId || !messages?.length) return messages;

  let result = messages.map((m) => ({ ...m }));

  const needsRunMatch = result.some(
    (m) => m.role === "agent" && !m.uiComponents?.length && m.runId,
  );
  if (!needsRunMatch) return result;

  try {
    const state = await fetchAguiStreamState(threadId);
    const runSnapshots = extractRunUiSnapshots(state);
    if (runSnapshots.length) {
      result = applyRunSnapshotsToMessages(result, runSnapshots);
    }
  } catch {
    /* optional */
  }

  const stillNeedsUi = result.some(
    (m) => m.role === "agent" && !m.uiComponents?.length && m.runId,
  );
  if (stillNeedsUi && options.includeTrackingState) {
    try {
      const trackingState = await fetchTrackingAguiStreamState(threadId);
      const trackingRuns = extractRunUiSnapshots(trackingState);
      if (trackingRuns.length) {
        result = applyRunSnapshotsToMessages(result, trackingRuns);
      }
    } catch {
      /* optional */
    }
  }

  return result;
}

export async function mapAndEnrichChatMessages(apiRows, threadId, options = {}) {
  const mapped = (apiRows ?? []).map(mapChatApiMessageToViewModel);
  const enriched = await enrichMessagesWithAguiState(mapped, threadId, options);
  return sanitizeEnrichedChatMessages(enriched);
}
